import React, { useState, useEffect, useCallback } from 'react';
import { updateDatasetTrim } from '../services/datasetsService';
import { useError } from '../contexts/ErrorContext';
import Button from './Button';
import './TrimPanel.css';

const TrimPanel = ({ datasetId, locations, currentTime, dataset, onTrimChange }) => {
  const { addError } = useError();
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fullStart = locations?.[0]?.timestamp || null;
  const fullEnd = locations?.[locations.length - 1]?.timestamp || null;

  // Initialize from persisted trim or full range
  useEffect(() => {
    if (dataset?.trimStartTime != null && dataset?.trimEndTime != null) {
      setStartTime(dataset.trimStartTime);
      setEndTime(dataset.trimEndTime);
    } else if (fullStart != null && fullEnd != null) {
      setStartTime(fullStart);
      setEndTime(fullEnd);
    }
  }, [dataset, fullStart, fullEnd]);

  const isTrimmed = dataset?.trimStartTime != null && dataset?.trimEndTime != null;

  const formatTime = (timestamp) => {
    if (timestamp == null) return '--:--:--';
    return new Date(timestamp * 1000).toLocaleTimeString();
  };

  const formatDuration = (seconds) => {
    if (seconds == null || seconds <= 0) return '--';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const handleSetStart = () => {
    if (currentTime != null) {
      setStartTime(currentTime);
      setDirty(true);
    }
  };

  const handleSetEnd = () => {
    if (currentTime != null) {
      setEndTime(currentTime);
      setDirty(true);
    }
  };

  const handleSave = useCallback(async () => {
    if (startTime == null || endTime == null || startTime >= endTime) return;
    setSaving(true);
    try {
      await updateDatasetTrim(datasetId, startTime, endTime, addError);
      setDirty(false);
      if (onTrimChange) onTrimChange({ trimStartTime: startTime, trimEndTime: endTime });
    } catch {
      // error already handled by service
    } finally {
      setSaving(false);
    }
  }, [datasetId, startTime, endTime, addError, onTrimChange]);

  const handleClear = useCallback(async () => {
    setSaving(true);
    try {
      await updateDatasetTrim(datasetId, null, null, addError);
      setStartTime(fullStart);
      setEndTime(fullEnd);
      setDirty(false);
      if (onTrimChange) onTrimChange({ trimStartTime: null, trimEndTime: null });
    } catch {
      // error already handled
    } finally {
      setSaving(false);
    }
  }, [datasetId, fullStart, fullEnd, addError, onTrimChange]);

  const trimDuration = startTime != null && endTime != null ? endTime - startTime : null;
  const fullDuration = fullStart != null && fullEnd != null ? fullEnd - fullStart : null;
  const isValid = startTime != null && endTime != null && startTime < endTime;

  return (
    <div className="trim-panel">
      <div className="trim-panel-section">
        <h3 className="trim-panel-title">Trim Dataset</h3>
        <p className="trim-panel-desc">
          Scrub the timeline to the desired position, then click Set Start or Set End.
        </p>
      </div>

      <div className="trim-panel-current">
        <span className="trim-panel-label">Current Position</span>
        <span className="trim-panel-value">{formatTime(currentTime)}</span>
      </div>

      <div className="trim-panel-times">
        <div className="trim-panel-time-row">
          <div className="trim-panel-time-info">
            <span className="trim-panel-label">Start</span>
            <span className="trim-panel-value">{formatTime(startTime)}</span>
          </div>
          <Button variant="outline" size="small" onClick={handleSetStart}>
            Set Start
          </Button>
        </div>
        <div className="trim-panel-time-row">
          <div className="trim-panel-time-info">
            <span className="trim-panel-label">End</span>
            <span className="trim-panel-value">{formatTime(endTime)}</span>
          </div>
          <Button variant="outline" size="small" onClick={handleSetEnd}>
            Set End
          </Button>
        </div>
      </div>

      <div className="trim-panel-summary">
        <div className="trim-panel-stat">
          <span className="trim-panel-label">Trimmed Duration</span>
          <span className="trim-panel-value">{formatDuration(trimDuration)}</span>
        </div>
        <div className="trim-panel-stat">
          <span className="trim-panel-label">Full Duration</span>
          <span className="trim-panel-value trim-panel-muted">{formatDuration(fullDuration)}</span>
        </div>
      </div>

      <div className="trim-panel-actions">
        <Button
          variant="primary"
          size="medium"
          onClick={handleSave}
          disabled={!isValid || saving || !dirty}
        >
          {saving ? 'Saving...' : 'Save Trim'}
        </Button>
        {isTrimmed && (
          <Button
            variant="outline"
            size="medium"
            onClick={handleClear}
            disabled={saving}
          >
            Clear Trim
          </Button>
        )}
      </div>

      {isTrimmed && !dirty && (
        <div className="trim-panel-status">
          Dataset is trimmed
        </div>
      )}
    </div>
  );
};

export default TrimPanel;
